import {
  createBox,
  createText,
  useTheme as useRestyleTheme,
} from '@shopify/restyle'
import {
  type ComponentProps,
  type ComponentRef,
  createElement,
  forwardRef,
} from 'react'

import type { FunkitTheme } from './toRestyleTheme'
import type { FunkitFontWeight } from './tokens'

/**
 * `@shopify/restyle` primitives bound to the connect-rn {@link FunkitTheme}.
 * Components use these instead of raw `View` / `Text` so style props
 * (`backgroundColor`, `padding`, `borderRadius`, `variant`, …) resolve to theme
 * tokens with full type safety.
 */
export const Box = createBox<FunkitTheme>()

const RestyleText = createText<FunkitTheme>()

export interface TextProps extends ComponentProps<typeof RestyleText> {
  /**
   * Override the font weight for this node regardless of `variant`. Applies the
   * resolved per-cut family AND numeric weight — both are required, since RN/Expo
   * can't select a custom font's weight via `fontWeight` alone (see
   * {@link fontFamilyForWeight}). So `variant="detail" weight="medium"` renders
   * the detail style in `Inter-Medium`. An explicit `style` still wins over it.
   */
  weight?: FunkitFontWeight
}

/**
 * {@link FunkitTheme}-bound Restyle `Text` with a `weight` escape hatch: set
 * `weight` to render a single node at a different cut than its `variant` without
 * minting a `*Medium` twin variant. Without `weight` it's the plain Restyle Text.
 */
export const Text = forwardRef<ComponentRef<typeof RestyleText>, TextProps>(
  function Text({ weight, variant, style, ...rest }, ref) {
    const theme = useRestyleTheme<FunkitTheme>()
    // Apply the weight override *before* `style` so a caller's explicit style
    // still wins; `?.` guards mocked themes that omit `textWeights`.
    const weightStyle = weight ? theme.textWeights?.[weight] : undefined
    // Text color isn't a Restyle color prop — the theme has no `colors` bucket;
    // colors live under the Figma groups. Default it here from `content/elements`
    // by variant (detail/subtitle are secondary, the rest primary); an explicit
    // `style` color still wins. `?.` guards mocked themes.
    const elements = theme.content?.elements
    const secondary = variant === 'detail' || variant === 'headerSubtitle'
    const colorStyle = elements
      ? { color: secondary ? elements.secondary : elements.primary }
      : undefined
    return createElement(RestyleText, {
      ref,
      variant,
      style: [colorStyle, weightStyle, style],
      ...rest,
    })
  },
)

/** {@link FunkitTheme}-typed `useTheme`, for reading raw token values. */
export const useTheme = () => useRestyleTheme<FunkitTheme>()
